import React from "react"

import { useTranslation } from "@bounce/i18n"

import { BookNow } from "./components/BookNow"
import { CouponBanner } from "./components/CouponBanner/CouponBanner"
import { Events } from "./components/Events"
import { FAQs } from "./components/FAQs"
import Hero from "./components/Hero"
import { Places } from "./components/Places"
import { useBreadcrumbs } from "./hooks/useBreadcrumbs"
import { useInterpolations } from "./hooks/useInterpolations"
import { useSEO } from "./hooks/useSEO"
import { ProductBanner } from "../../Generic/ArticleScreen/components/ProductBanner/ProductBanner"
import { RichTextSection } from "@/components/Generic/RichText/RichTextSection/RichTextSection"
import { EventStructuredData } from "@/components/Generic/StructuredData/EventStructuredData"
import { Layout } from "@/components/LuggageStorage/Layout/Layout"
import StoreSliderSection from "@/components/LuggageStorage/StoreSlider/StoreSliderSection"
import { StickyCTAProvider } from "@/contexts/StickyCTAContext"
import type { CityData } from "@/libs/bounce/cities"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"
import type { AggregateRating } from "@/libs/bounce/types"
import type { FormattedEvent } from "@/libs/prismic/event/types"
import { useUrl } from "@/libs/router"
import { useAppURL } from "@/utils/appURL"

export type EventScreenProps = {
  event: FormattedEvent
  city: CityData
  relatedEvents: FormattedEvent[]
  aggregateRating: AggregateRating
  nearestEventStores: NearbyStorageSpot[]
}

const EventScreen = ({ event, city, relatedEvents, aggregateRating, nearestEventStores }: EventScreenProps) => {
  const { t } = useTranslation("marketing-ls-event")
  const breadcrumbs = useBreadcrumbs({ city, event })
  const interpolations = useInterpolations({ event, city })
  const headProps = useSEO({ city, event, interpolations })
  const buildAppURL = useAppURL()
  const { getUrl } = useUrl()

  const publicationDate = event.dateModified || event.datePublished
  const primaryButtonLabel = t("screen.event.hero.findBagStorage", "Find bag storage")
  const searchQuery = buildAppURL(getUrl({ path: "/s" }), {
    query: event.address || city.query,
    coupon: event.couponCode,
  })

  return (
    <StickyCTAProvider
      config={{
        from: "Event",
        searchQuery: event.address,
        label: primaryButtonLabel,
      }}>
      <Layout languages={event.languages} headProps={headProps} breadcrumbs={breadcrumbs} hideNewsletterBanner>
        <Hero
          event={event}
          city={city}
          interpolations={interpolations}
          aggregateRating={aggregateRating}
          primaryButtonLabel={primaryButtonLabel}
        />
        {!!nearestEventStores.length && (
          <StoreSliderSection
            name="NearestEventStores"
            stores={nearestEventStores}
            from="Event"
            lowestPrice={interpolations.LOWEST_PRICE}
            path={headProps.url}
            searchQuery={searchQuery}
            subtitle={t(
              "screen.event.storeSuggestions.description",
              "Bounce can help with securely store your bags while you enjoy the event.",
            )}
            title={t("screen.event.storeSuggestions.titleV2", "Where to drop your bags near \n{{eventName}}?", {
              eventName: event.name,
            })}
            className="py-section"
            sectionViewedMeta={{ section: "EventNearestStores" }}
            renderStructuredData={false}
          />
        )}
        <CouponBanner event={event} />
        <div id="about-event" />
        <RichTextSection
          className="pt-section"
          disableImgBlueFilter={true}
          field={event.content}
          interpolations={interpolations}
          publicationDate={publicationDate}
        />
        <BookNow event={event} city={city} className="mt-section" />
        <ProductBanner />
        <FAQs faqs={event.faqs} className="pt-section" />
        <Places city={city} className="pt-section" />
        <Events events={relatedEvents} cityName={city.name} citySlug={city.slug} className="pt-section" />
        <div className="mt-28 md:mt-36" />
        <EventStructuredData event={event} city={city} />
      </Layout>
    </StickyCTAProvider>
  )
}

export default EventScreen
